<template>
    <div class="target-build-platform-summary">
        <b-loading is-full-page :active="isLoading"/>
        <b-notification v-if="error" type="is-danger is-light" aria-close-label="Close notification" role="alert">
            {{ error }}
        </b-notification>
        <div class="card">
            <div class="card-content">
                <div class="columns is-tablet">
                    <div class="column">
                        <b-field label="Products">
                            <b-taginput
                                v-model="selectedProducts"
                                :data="filteredProducts"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a product"
                                @typing="queries.product = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Branch">
                            <b-taginput
                                v-model="selectedBranches"
                                :data="filteredBranches"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a branch"
                                @typing="queries.branch = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Commit id">
                            <b-input v-model="filters.commit_id"/>
                        </b-field>
                        <b-field label="Target (regexp)">
                            <b-input v-model="filters.target_name"/>
                        </b-field>
                        <b-switch v-model="filters.runs_with_additional_tools">
                            <template v-if="filters.runs_with_additional_tools">
                                Show runs with additional tools only
                            </template>
                            <template v-else>
                                Hide runs with additional tools
                            </template>
                        </b-switch>
                        <b-field label="Additional tools" v-if="filters.runs_with_additional_tools">
                            <b-taginput
                                v-model="selectedAdditionalTools"
                                :data="filteredAdditionalTools"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add an additional tool"
                                @typing="queries.additionalTool = $event">
                            </b-taginput>
                        </b-field>
                    </div>
                    <div class="column">
                        <b-field label="CI service">
                            <b-taginput
                                v-model="selectedCIServices"
                                :data="filteredCIServices"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a CI service"
                                @typing="queries.ciService = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Build trigger">
                            <b-taginput
                                v-model="selectedBuildTypes"
                                :data="filteredBuildTypes"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a build trigger"
                                @typing="queries.buildType = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Test suites">
                            <b-taginput
                                v-model="selectedTestSuiteNames"
                                :data="filteredTestSuiteNames"
                                autocomplete
                                open-on-focus
                                icon="label"
                                field="name"
                                placeholder="Add a test suite"
                                @typing="queries.testSuiteName = $event">
                            </b-taginput>
                        </b-field>
                        <b-field label="Time interval">
                            <div class="field">
                                <b-select expanded v-model="filters.time_interval">
                                    <option
                                        v-for="option in TIME_INTERVAL_OPTIONS"
                                        :value="option.value.toString()"
                                        :key="option.value">
                                        {{ option.title }}
                                    </option>
                                </b-select>
                                <b-datepicker
                                        v-if="filters.time_interval === '-2'"
                                        placeholder="Click to select..."
                                        v-model="filters.dates"
                                        icon="calendar-today"
                                        range>
                                </b-datepicker>
                            </div>
                        </b-field>
                    </div>
                </div>

                <b-button type="is-primary" @click="onSearchClick">Search</b-button>
            </div>
        </div>
        <br>
        <b-pagination
                id="tablePagination"
                :total="testRunsTargetBuildsTotalCount"
                :current="currentPage"
                size="is-small"
                v-on:change="onChangePage"
                :per-page="PER_PAGE"
                order="is-right"
                aria-next-label="Next page"
                aria-previous-label="Previous page"
                aria-page-label="Page"
                aria-current-label="Current page">
        </b-pagination>
        <target-builds-platforms-table :platforms="testResults.length > 0 ? testRunsPlatforms : []"
                                       :test-results="testResults"
                                       :target-builds="testRunsTargetBuilds"
                                       v-on:select-target-build="onSelectTargetBuild"
                                       :link-generator="generateResultsLink"
                                       v-if="!isLoading">
            <template #empty-message>
                <span v-if="!dataIsLoaded">
                    Set filters and click "Search"
                </span>
                <span v-else>
                    No results found
                </span>
            </template>
        </target-builds-platforms-table>
        <b-modal :active.sync="showTargetBuildDetails" :width="600">
            <div class="card">
                <div class="card-content">
                    <target-build-show v-if="currentTargetBuildId" :target-build-id="currentTargetBuildId"/>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import TargetBuildsPlatformsTable from "../components/TargetBuildsPlatformsTable";
    import TargetBuildShow from "./TargetBuildShow";
    import {
        TIME_INTERVAL_OPTIONS,
        TARGET_BUILDS_PLATFORMS_SUMMARY_PER_PAGE,
    } from "../utils/constants";
    import {mapActions, mapGetters, mapMutations} from 'vuex';
    import {valueToArray, formatDate} from "../utils/helpers";
    import filtersMixin from "../utils/filtersMixin";

    export default {
        name: "TargetBuildPlatformSummary",
        mixins: [filtersMixin],
        data() {
            return {
                isLoading: false,
                error: null,
                showTargetBuildDetails: false,
                currentTargetBuildId: null,
                filters: {},
                TIME_INTERVAL_OPTIONS,
                PER_PAGE: TARGET_BUILDS_PLATFORMS_SUMMARY_PER_PAGE,
                dataIsLoaded: false,

                queries: {
                    product: '',
                    branch: '',
                    target_name: '',
                    ciService: '',
                    testSuiteName: '',
                    buildType: '',
                    additionalTool: '',
                },
            }
        },
        computed: {
            ...mapGetters('Platforms', [
                'testRunsPlatforms',
            ]),
            ...mapGetters('TargetBuilds', [
                'testRunsTargetBuilds',
                'testRunsTargetBuildsTotalCount',
            ]),
            ...mapGetters('TestResults', [
                'testResults',
            ]),
            ...mapGetters('Auth', [
                'hasAccess',
            ]),
            currentPage() {
                return parseInt(this.$route.query.page || 1);
            },
        },
        methods: {
            ...mapActions('Platforms', [
                'fetchPlatforms',
            ]),
            ...mapActions('TargetBuilds', [
                'fetchBranches',
                'fetchProducts',
                'fetchBuildTypes',
                'fetchAdditionalTools',
                'fetchCIServices',
            ]),
            ...mapActions('TestResults', [
                'fetchTargetBuildsPlatformsSummary',
            ]),
            ...mapMutations('TestResults', [
                'CLEAR_TEST_RESULTS',
            ]),
            ...mapMutations('TargetBuilds', [
                'CLEAR_TEST_RUNS_TARGET_BUILDS',
            ]),
            ...mapMutations('Platforms', [
                'CLEAR_TEST_RUNS_PLATFORMS',
            ]),
            ...mapActions('TestRuns', [
                'fetchMtrNames',
            ]),
            ...mapActions('TestSuites', [
                'fetchTestSuiteNames',
            ]),
            setFiltersByQuery(query) {
                this.filters = {
                    target_ids: valueToArray(query.target_ids),
                    branch_ids: valueToArray(query.branch_ids),
                    mtr_ids: valueToArray(query.mtr_ids),
                    test_suite_name_ids: valueToArray(query.test_suite_name_ids),
                    product_ids: query.product_ids == null ? ["2"] : valueToArray(query.product_ids),
                    time_interval: query.time_interval == null ? '-1' : query.time_interval,
                    dates: valueToArray(query.dates).map(item => new Date(item)),
                    ci_service_ids: valueToArray(query.ci_service_ids),
                    commit_id: query.commit_id || '',
                    target_name: query.target_name || '',
                    build_type_ids: valueToArray(query.build_type_ids),
                    additional_tool_ids: valueToArray(query.additional_tool_ids),
                    runs_with_additional_tools: query.runs_with_additional_tools === 'true',
                }
            },
            async search() {
                if (!this.hasAccess) return;
                this.isLoading = true;
                const filters = Object.assign({}, this.filters, {
                    page: this.currentPage,
                    per_page: this.PER_PAGE,
                    dates: this.filters.dates.map(date => formatDate(date)),
                });
                try {
                    await this.fetchTargetBuildsPlatformsSummary({ filters });
                    this.dataIsLoaded = true;
                } catch(message) {
                    this.error = message;
                }
                this.isLoading = false;
            },
            fetchData() {
                return [
                    this.fetchBranches(),
                    this.fetchTestSuiteNames(),
                    this.fetchProducts(),
                    this.fetchBuildTypes(),
                    this.fetchAdditionalTools(),
                    this.fetchCIServices(),
                ];
            },
            async setFiltersByName() {},
            onSelectTargetBuild(targetBuildId) {
                this.currentTargetBuildId = targetBuildId;
                this.showTargetBuildDetails = true;
            },
            async onChangePage(page) {
                const query = Object.assign({}, this.$route.query, { page });
                await this.$router.push({ name: this.$route.name, query });
                await this.search();
                this.$scrollTo('#tablePagination');
            },
            generateResultsLink(platformId, targetName, mtrId) {
                return { name: 'TestRunIndex', query: {
                    platform_ids: [platformId],
                    target_name: targetName,
                    mtr_ids: [mtrId],
                    test_suite_name_ids: this.filters.test_suite_name_ids,
                    show_skipped_results: true,
                } };
            },
            clearData() {
                this.CLEAR_TEST_RUNS_TARGET_BUILDS();
                this.CLEAR_TEST_RUNS_PLATFORMS();
                this.CLEAR_TEST_RESULTS();
            },
            async afterSearch() {
                this.$scrollTo('#tablePagination');
            }
        },
        components: {
            TargetBuildsPlatformsTable,
            TargetBuildShow,
        }
    }
</script>

<style lang="scss" scoped>

</style>